import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { BookOpen, FileText, Lock, Send, Sparkles, X } from 'lucide-react';
import { useAiAssist, useAiStatus } from '@/api/ai';
import { useReply, useTicketMessages } from '@/api/tickets';
import { useAllUsers } from '@/api/users';
import type { AiResult, Message, Ticket, TicketStatus } from '@/api/types';
import { Avatar, Button, ErrorState, Spinner, useToast } from '@/components/ui';
import { useAuth } from '@/app/auth/useAuth';
import { useLiveTicket } from '@/app/realtime/realtime-context';
import { cn } from '@/lib/cn';
import { formatDateTime, formatTime } from '@/lib/format';
import { STATUS_LABEL } from './meta';
import { ArticleCard, StatusEvent, TicketCard } from '@/components/messages/MessageCards';
import { ComposerExtras } from './ComposerExtras';

/** Replies posted by the AI assistant carry the AI agent's name in their meta. */
const aiAgentName = (m: Message): string | null =>
  m.meta?.source === 'ai_assistant'
    ? `${typeof m.meta.agent === 'string' && m.meta.agent ? m.meta.agent : 'AI assistant'} (AI)`
    : null;

/** Messages are grouped under a day divider when the date changes. */
const dayOf = (iso: string) =>
  new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeZone: 'Asia/Kolkata' }).format(new Date(iso));

export function Conversation({
  ticket,
  variant = 'ticket',
}: {
  ticket: Ticket;
  /** `chat` shows compact bubbles and keeps the ticket open after replying. */
  variant?: 'ticket' | 'chat';
}) {
  const messages = useTicketMessages(ticket.id);
  // New messages (visitor, AI bot, teammates) arrive over the socket — no polling.
  const { assistantTyping, assistantName } = useLiveTicket(ticket.id);
  const users = useAllUsers();
  const userById = new Map((users.data ?? []).map((u) => [u.id, u]));
  const bottom = useRef<HTMLDivElement>(null);
  const count = messages.data?.length ?? 0;

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [count, ticket.id, assistantTyping]);

  const authorName = (m: Message) =>
    m.authorType === 'customer'
      ? (ticket.customer?.name ?? 'Customer')
      : m.authorId
        ? (userById.get(m.authorId)?.name ?? 'Agent')
        : (aiAgentName(m) ?? 'System');

  let lastDay = '';
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6" aria-live="polite">
        {messages.isPending ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" label="Loading conversation" />
          </div>
        ) : messages.error ? (
          <ErrorState error={messages.error} onRetry={() => void messages.refetch()} />
        ) : (
          <ol className="space-y-3">
            {messages.data.map((m) => {
              const day = dayOf(m.createdAt);
              const divider = day !== lastDay;
              lastDay = day;
              return (
                <li key={m.id}>
                  {divider && (
                    <div className="my-4 flex items-center gap-3 text-[11px] font-medium text-slate-400">
                      <span className="h-px flex-1 bg-slate-200" />
                      {day}
                      <span className="h-px flex-1 bg-slate-200" />
                    </div>
                  )}
                  <MessageItem
                    message={m}
                    author={authorName(m)}
                    avatarUrl={m.authorType === 'customer' ? ticket.customer?.avatarUrl : undefined}
                    compact={variant === 'chat'}
                  />
                </li>
              );
            })}
          </ol>
        )}
        {assistantTyping && (
          <div role="status" className="mt-3 flex items-center gap-2 text-xs text-violet-600">
            <Sparkles className="h-3.5 w-3.5 animate-pulse" aria-hidden />
            {assistantName
              ? `${assistantName} (AI) is drafting a reply…`
              : 'AI assistant is drafting a reply…'}
          </div>
        )}
        <div ref={bottom} />
      </div>
      <Composer ticket={ticket} variant={variant} />
    </div>
  );
}

function MessageItem({
  message: m,
  author,
  avatarUrl,
  compact,
}: {
  message: Message;
  author: string;
  avatarUrl?: string | null;
  compact: boolean;
}) {
  if (m.authorType === 'system') {
    // Status changes render as a timeline chip; other system notes (assignment, team) as a quiet line.
    const status = m.body
      .split('\n')
      .find((l) => l.startsWith('Status: '))
      ?.split('→')[1]
      ?.trim();
    const rest = m.body
      .split('\n')
      .filter((l) => !l.startsWith('Status: '))
      .join(' · ');
    if (status && !rest) return <StatusEvent status={status} at={formatTime(m.createdAt)} audience="agent" />;
    return (
      <p
        className="text-center text-xs whitespace-pre-line text-slate-400"
        title={formatDateTime(m.createdAt)}
      >
        {m.body.split('\n').join(' · ')} · {formatTime(m.createdAt)}
      </p>
    );
  }
  const mine = m.authorType === 'agent';
  return (
    <div className={cn('flex gap-2.5', mine && 'flex-row-reverse')}>
      {!compact && <Avatar name={author} src={avatarUrl} size="sm" />}
      <div
        className={cn(
          'max-w-[85%] min-w-0',
          compact ? 'sm:max-w-[70%]' : 'sm:max-w-[75%]',
          mine && 'text-right',
        )}
      >
        <div className={cn('mb-1 flex items-baseline gap-2 text-xs', mine && 'flex-row-reverse')}>
          <span className="font-semibold text-slate-700">{author}</span>
          <time dateTime={m.createdAt} title={formatDateTime(m.createdAt)} className="text-slate-400">
            {formatTime(m.createdAt)}
          </time>
          {m.isInternal && (
            <span className="inline-flex items-center gap-1 font-medium text-amber-700">
              <Lock className="h-3 w-3" aria-hidden /> Internal note
            </span>
          )}
        </div>
        {m.kind === 'ticket' ? (
          <div className={cn('flex', mine && 'justify-end')}>
            <TicketCard meta={m.meta} body={m.body} />
          </div>
        ) : m.kind === 'article' ? (
          <div className={cn('flex', mine && 'justify-end')}>
            <ArticleCard meta={m.meta} body={m.body} />
          </div>
        ) : (
          <div
            className={cn(
              'inline-block rounded-2xl px-3.5 py-2.5 text-left text-sm break-words whitespace-pre-wrap',
              m.isInternal
                ? 'border border-amber-200/80 bg-amber-50 text-amber-950 shadow-xs'
                : mine
                  ? 'from-brand-500 to-brand-600 shadow-brand-600/20 rounded-tr-md bg-gradient-to-br text-white shadow-md'
                  : 'rounded-tl-md border border-slate-200/80 bg-white text-slate-800 shadow-sm',
            )}
          >
            {m.body}
          </div>
        )}
      </div>
    </div>
  );
}

type AfterSend = TicketStatus | 'keep';

function Composer({ ticket, variant }: { ticket: Ticket; variant: 'ticket' | 'chat' }) {
  const { can } = useAuth();
  const toast = useToast();
  const reply = useReply(ticket.id);
  const [mode, setMode] = useState<'reply' | 'note'>('reply');
  const [body, setBody] = useState('');
  // Chats stay open while the conversation continues; email-style tickets wait on the customer.
  const [after, setAfter] = useState<AfterSend>(variant === 'chat' ? 'keep' : 'pending');
  const draftKey = `ks:draft:${ticket.id}`;
  const aiStatus = useAiStatus(can('use', 'AiAssistant'));
  const assist = useAiAssist(ticket.id);
  const [summary, setSummary] = useState<AiResult | null>(null);
  const [draftInfo, setDraftInfo] = useState<AiResult | null>(null);
  const [article, setArticle] = useState<{ id: string; title: string; excerpt: string } | null>(null);

  useEffect(() => {
    try {
      setBody(sessionStorage.getItem(draftKey) ?? '');
    } catch {
      setBody('');
    }
  }, [draftKey]);

  const update = (v: string) => {
    setBody(v);
    try {
      if (v) sessionStorage.setItem(draftKey, v);
      else sessionStorage.removeItem(draftKey);
    } catch {
      // Draft persistence is a convenience only.
    }
  };

  if (!can('update', 'Ticket')) return null;
  const note = mode === 'note';

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    const text = body.trim() || (article && !note ? article.title : '');
    if (!text || reply.isPending) return;
    const status: TicketStatus | undefined = note ? undefined : after === 'keep' ? ticket.status : after;
    reply.mutate(
      { body: text, isInternal: note, status, articleId: !note && article ? article.id : undefined },
      {
        onSuccess: () => {
          update('');
          setDraftInfo(null);
          setArticle(null);
          if (!note && status && status !== ticket.status)
            toast.success(`Sent · marked ${STATUS_LABEL[status]}`);
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  const suggest = () =>
    assist.mutate('suggest-reply', {
      onSuccess: (r) => {
        setMode('reply');
        update(r.text);
        setDraftInfo(r);
        toast.info('Draft ready — review and edit before sending');
      },
      onError: (e) => toast.error(e.message),
    });
  const summarize = () =>
    assist.mutate('summarize', { onSuccess: setSummary, onError: (e) => toast.error(e.message) });
  const busy = assist.isPending ? assist.variables : null;

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey || (variant === 'chat' && !e.shiftKey))) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <form onSubmit={submit} className="border-t border-slate-200/70 bg-white/90 p-3 backdrop-blur sm:p-4">
      {summary && (
        <div className="from-brand-50 to-accent-50/60 ring-brand-100 relative mb-3 rounded-2xl bg-gradient-to-br p-3.5 pr-10 ring-1">
          <p className="text-brand-700 mb-1 flex items-center gap-1.5 text-xs font-semibold">
            <FileText className="h-3.5 w-3.5" /> Summary · {summary.model}
          </p>
          <p className="text-sm whitespace-pre-line text-slate-700">{summary.text}</p>
          <button
            type="button"
            onClick={() => setSummary(null)}
            aria-label="Dismiss summary"
            className="absolute top-2.5 right-2.5 rounded-md p-1 text-slate-400 hover:bg-white/70 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <div
        className={cn(
          'rounded-2xl border bg-white shadow-sm transition-all focus-within:ring-4',
          note
            ? 'border-amber-300 bg-amber-50/60 focus-within:ring-amber-400/15'
            : 'focus-within:border-brand-400 focus-within:ring-brand-500/10 border-slate-200',
        )}
      >
        <div
          role="tablist"
          aria-label="Message type"
          className="flex gap-1 border-b border-inherit px-2 pt-2"
        >
          {(['reply', 'note'] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={mode === m}
              onClick={() => setMode(m)}
              className={cn(
                '-mb-px rounded-t-md border-b-2 px-3 py-1.5 text-xs font-semibold',
                mode === m
                  ? m === 'note'
                    ? 'border-amber-500 text-amber-700'
                    : 'border-brand-500 text-brand-700'
                  : 'border-transparent text-slate-500 hover:text-slate-800',
              )}
            >
              {m === 'reply'
                ? `Reply to ${ticket.customer?.name.split(' ')[0] ?? 'customer'}`
                : 'Internal note'}
            </button>
          ))}
          {aiStatus.data?.available && (
            <span className="ml-auto flex items-center gap-1 pb-1">
              {aiStatus.data.replySuggestions && (
                <button
                  type="button"
                  onClick={suggest}
                  disabled={!!busy}
                  title={`Draft with ${aiStatus.data.provider?.name ?? 'AI'}`}
                  className="text-brand-700 hover:bg-brand-50 flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold disabled:opacity-60"
                >
                  {busy === 'suggest-reply' ? <Spinner size="sm" /> : <Sparkles className="h-3.5 w-3.5" />}
                  {busy === 'suggest-reply' ? 'Drafting…' : 'Suggest reply'}
                </button>
              )}
              {aiStatus.data.summaries && (
                <button
                  type="button"
                  onClick={summarize}
                  disabled={!!busy}
                  className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-60"
                >
                  {busy === 'summarize' ? <Spinner size="sm" /> : <FileText className="h-3.5 w-3.5" />}
                  Summarize
                </button>
              )}
            </span>
          )}
        </div>
        <label htmlFor={`composer-${ticket.id}`} className="sr-only">
          {note ? 'Internal note' : 'Reply'}
        </label>
        <textarea
          id={`composer-${ticket.id}`}
          value={body}
          onChange={(e) => update(e.target.value)}
          onKeyDown={onKeyDown}
          rows={variant === 'chat' ? 2 : 4}
          placeholder={
            note ? 'Only your team can see this…' : 'Type your reply… use {{first_name}} to personalise'
          }
          className="block w-full resize-y bg-transparent px-3 py-2.5 text-sm placeholder:text-slate-400 focus:outline-none"
        />
        {article && !note && (
          <div className="px-3 pb-2">
            <span className="bg-brand-50 text-brand-700 ring-brand-100 inline-flex max-w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs ring-1">
              <BookOpen className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate font-medium">{article.title}</span>
              <button
                type="button"
                onClick={() => setArticle(null)}
                aria-label="Remove article"
                className="hover:text-brand-900"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2 px-2 pb-2">
          <ComposerExtras
            allowArticles={!note}
            onInsert={(v) => update(body ? `${body}${body.endsWith(' ') ? '' : ' '}${v}` : v)}
            onPickArticle={setArticle}
          />
          {draftInfo && body ? (
            <span className="text-brand-700 flex min-w-0 items-center gap-1 text-[11px]">
              <Sparkles className="h-3 w-3 shrink-0" />
              <span className="truncate">
                AI draft · {draftInfo.model}
                {draftInfo.sources.length > 0 &&
                  ` · based on: ${draftInfo.sources.map((x) => x.title).join(', ')}`}
              </span>
            </span>
          ) : (
            <span className="hidden text-[11px] text-slate-400 sm:inline">
              {variant === 'chat' ? 'Enter to send · Shift+Enter for a new line' : '⌘/Ctrl + Enter to send'}
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            {!note && (
              <select
                aria-label="Status after sending"
                value={after}
                onChange={(e) => setAfter(e.target.value as AfterSend)}
                className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700"
              >
                <option value="keep">Keep as {STATUS_LABEL[ticket.status]}</option>
                {(['pending', 'on_hold', 'solved'] as const)
                  .filter((s) => s !== ticket.status)
                  .map((s) => (
                    <option key={s} value={s}>
                      Send & mark {STATUS_LABEL[s]}
                    </option>
                  ))}
              </select>
            )}
            <Button
              type="submit"
              size="sm"
              loading={reply.isPending}
              disabled={!body.trim() && !(article && !note)}
            >
              {note ? <Lock className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
              {note ? 'Add note' : 'Send'}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}
